import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { useNavigate } from "react-router-dom";
import { Clock } from "@phosphor-icons/react";

const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000; // Warn 2 minutes before logout (i.e. at 58 min)

/**
 * Global idle-logout watchdog. Listens for user activity and:
 *   • Shows a warning dialog 2 minutes before the timeout kicks in.
 *   • Logs the user out automatically at IDLE_TIMEOUT_MS.
 *
 * Rendered inside <AppShell> so it's only active for logged-in users.
 */
export default function IdleLogout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [warn, setWarn] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_BEFORE_MS / 1000);
  const warnRef = useRef(false);
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const tickTimer = useRef(null);

  function clearTimers() {
    if (warnTimer.current) {
      clearTimeout(warnTimer.current);
      warnTimer.current = null;
    }
    if (logoutTimer.current) {
      clearTimeout(logoutTimer.current);
      logoutTimer.current = null;
    }
    if (tickTimer.current) {
      clearInterval(tickTimer.current);
      tickTimer.current = null;
    }
  }

  function scheduleTimers() {
    clearTimers();
    warnTimer.current = setTimeout(showWarning, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);
    logoutTimer.current = setTimeout(doLogout, IDLE_TIMEOUT_MS);
  }

  function showWarning() {
    warnRef.current = true;
    setWarn(true);
    setCountdown(WARNING_BEFORE_MS / 1000);
    tickTimer.current = setInterval(() => {
      setCountdown((c) => Math.max(0, c - 1));
    }, 1000);
  }

  async function doLogout() {
    clearTimers();
    warnRef.current = false;
    setWarn(false);
    await logout();
    navigate("/login");
  }

  function keepActive() {
    warnRef.current = false;
    setWarn(false);
    scheduleTimers();
  }

  useEffect(() => {
    if (!user) return undefined;
    scheduleTimers();
    const events = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];
    let throttle = null;
    function onActivity() {
      if (warnRef.current) return; // don't silently swallow warning
      if (throttle) return;
      throttle = setTimeout(() => {
        throttle = null;
      }, 5000);
      scheduleTimers();
    }
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = String(countdown % 60).padStart(2, "0");

  return (
    <Dialog open={warn} onOpenChange={(o) => !o && keepActive()}>
      <DialogContent className="max-w-sm" data-testid="idle-warning-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Clock size={20} className="text-pf-warning" /> {t("idleWarnTitle")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-pf-text-secondary">
            {t("idleWarnBody")}
          </p>
          <div className="text-3xl font-mono font-bold text-center tabular-nums text-pf-warning" data-testid="idle-countdown">
            {minutes}:{seconds}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={doLogout}
              className="pf-btn-ghost"
              data-testid="idle-logout-now"
            >
              {t("logOut")}
            </button>
            <button
              onClick={keepActive}
              className="pf-btn-primary"
              data-testid="idle-stay-btn"
            >
              {t("idleStay")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
